import { forwardRef, type InputHTMLAttributes } from 'react';
import { ukTodayYmd } from '@/utils/ukTime';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface DateRangePickerProps {
  /** Controlled start date (`YYYY-MM-DD`). */
  startDate: string;
  /** Controlled end date (`YYYY-MM-DD`). */
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  /** When set, renders a reset link after the inputs. */
  onReset?: () => void;
  /** Label for the reset link. Defaults to `Reset`. */
  resetLabel?: string;
  /** Dense sizing for tight headers. */
  compact?: boolean;
  /** `aria-label` for the start input. Defaults to `Start date`. */
  startLabel?: string;
  /** `aria-label` for the end input. Defaults to `End date`. */
  endLabel?: string;
  /** Composed via `cn()` (tailwind-merge) on the wrapper. */
  className?: string;
}

/**
 * The one date range. Two native token-styled date inputs that own
 * min/max clamping (start ≤ end ≤ today) via the native `min`/`max`
 * attributes — the picker UI offers no date outside the range — an
 * optional reset link (`Button` `variant="link"`), and an optional
 * compact mode. Stateless — the caller owns the values.
 */
const INPUT_BASE =
  'rounded-md border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

const INPUT_SIZE: Record<'regular' | 'compact', string> = {
  regular: 'px-2 py-1.5',
  compact: 'px-2 py-0.5',
};

export const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(
  function DateRangePicker(
    {
      startDate,
      endDate,
      onStartDateChange,
      onEndDateChange,
      onReset,
      resetLabel = 'Reset',
      compact = false,
      startLabel = 'Start date',
      endLabel = 'End date',
      className,
    },
    ref,
  ) {
    const today = ukTodayYmd();
    const inputClass = cn(INPUT_BASE, INPUT_SIZE[compact ? 'compact' : 'regular']);

    const startProps: InputHTMLAttributes<HTMLInputElement> = {
      type: 'date',
      value: startDate,
      max: endDate || today,
      'aria-label': startLabel,
      className: inputClass,
      onChange: (e) => onStartDateChange(e.target.value),
    };
    const endProps: InputHTMLAttributes<HTMLInputElement> = {
      type: 'date',
      value: endDate,
      min: startDate || undefined,
      max: today,
      'aria-label': endLabel,
      className: inputClass,
      onChange: (e) => onEndDateChange(e.target.value),
    };

    return (
      <div ref={ref} className={cn('flex flex-wrap items-center gap-2', className)}>
        <input {...startProps} />
        <span className="text-xs text-muted-foreground">to</span>
        <input {...endProps} />
        {onReset ? (
          <Button variant="link" type="button" onClick={onReset} className="h-auto p-0 text-xs">
            {resetLabel}
          </Button>
        ) : null}
      </div>
    );
  },
);
